import { ref, watch, onMounted, onUnmounted, type Ref } from 'vue';
import { serviceRequest } from '@cognitoforms/api/services/service-request';
import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
import type { SubscriptionResult } from 'src/utilities/websocket/websocket-types';

interface SyncStatus {
	syncing: boolean;
	progress: number;
	totalEntries: number;
	completedEntries: number;
	syncCount: number;
	failed?: boolean;
	completed?: boolean;
}

export function useLinkedLookupSync(formId: Ref<string | null>) {
	const syncStatus = ref<SyncStatus | null>(null);
	let subscription: SubscriptionResult | null = null;

	async function loadSyncStatus() {
		if (!formId.value) return;

		const response = await serviceRequest(
			`svc/entries-page/linked-lookup-sync-status/${formId.value}`, null, { method: 'get' }
		);

		// Check if we transitioned from syncing to completed
		const wasSyncing = syncStatus.value?.syncing;
		const isCompleted = wasSyncing && !response.syncing && !response.failed;

		if (isCompleted) {
			// Preserve the last known progress values, mark as completed
			syncStatus.value = {
				...syncStatus.value,
				syncing: false,
				completed: true,
				progress: 100
			};
		}
		else if (response.syncing || response.failed) {
			syncStatus.value = response;
		}
		else {
			syncStatus.value = null;
		}
	}

	function handleSyncEvent(entityIds: string[]) {
		if (!entityIds.includes(formId.value))
			return;
		loadSyncStatus();
	}

	function clearSyncStatus() {
		syncStatus.value = null;
	}

	watch(formId, () => {
		syncStatus.value = null;
		loadSyncStatus();
	});

	onMounted(async () => {
		await loadSyncStatus();
		subscription = websocketConnection.subscribeToEvents('LinkedLookupSync', handleSyncEvent);
	});

	onUnmounted(() => {
		subscription?.unsubscribe();
	});

	return {
		syncStatus,
		clearSyncStatus
	};
}
